import { Link, useParams } from "react-router-dom"
import { LocationDetails } from "source/groups/components/LocationDetails"
import { EventsList } from "source/groups/components/EventsList"
import { usePastEvents, useUpcomingEvents } from "../hooks/useEvents"
import {
  useCampusesForCurrentGroup,
  useCurrentGroup,
  useLocationForCurrentGroup,
} from "../hooks/useCurrentGroup"
import { useFirstPageOfLeaders } from "../hooks/useMemberships"
import { Heading, Subheading } from "@planningcenter/doxy-web"
import { useFirstPageOfTags, useTagGroupForTag } from "../hooks/useTags"
import { useApiRead } from "source/shared/SessionApiResource"
import {
  CrossAppEventResponse,
  Event,
} from "source/groups/my/groups/events/utils/crossAppEvents"

type Campus = {
  id: string
  attributes: {
    name: string
  }
}

type GroupResponse = {
  data: Group
}

type Group = {
  id: number
  attributes: {
    name: string
    description: string
    schedule: string
    list_leader_names_publicly: boolean
    location_type_preference: string
    virtual_location_url: string
  }
}

type EventResponse = {
  data: Event[]
  included: []
  meta: {
    total_count: number
  }
}

type Leader = {
  id: string
  attributes: {
    first_name: string
  }
}

type LeaderResponse = {
  data: Leader[]
}

type Tag = {
  id: string
  attributes: {
    name: string
  }
  relationships: {
    tag_group: {
      data: {
        type: string
        id: string
      }
    }
  }
}

type TagResponse = {
  data: Tag[]
}

export function About() {
  const { data: group } = useCurrentGroup() as GroupResponse
  const campuses = useCampusesForCurrentGroup()
  const { data: tags = [] } = useFirstPageOfTags() as TagResponse

  const hasCampuses = campuses.length > 0
  const hasDescription = !!group.attributes.description
  const hasPublishedTags = tags.length > 0
  const hasSchedule = !!group.attributes.schedule
  const shouldDisplayLeaders = group.attributes.list_leader_names_publicly

  return (
    <Wrapper>
      <RightColumn>
        {hasCampuses && <Campuses campuses={campuses} />}
        {hasPublishedTags && <Tags tags={tags} />}
        {hasSchedule && <Schedule />}
        {shouldDisplayLeaders && <LeadersIfAny />}
        <Location />
      </RightColumn>
      <LeftColumn>
        {hasDescription && <Description />}
        <EventsWrapper>
          <UpcomingEvents />
          <PastEvents />
        </EventsWrapper>
      </LeftColumn>
    </Wrapper>
  )
}

function Campuses({ campuses }: { campuses: Campus[] }) {
  return (
    <div className="mb-3">
      <Subheading level={2} text="Campus" />
      <div className="d-f fw-w g-4p mt-2">
        {campuses.map((campus) => (
          <span className="badge" key={campus.id}>
            {campus.attributes.name}
          </span>
        ))}
      </div>
    </div>
  )
}

function Description() {
  const {
    data: {
      attributes: { name, description },
    },
  } = useCurrentGroup() as GroupResponse

  return (
    <>
      <Heading level={2} size={3} text={`About ${name}`} />
      <div
        className="mt-2 mb-3 mb-4@md"
        data-preformatted-content="heading-scale:legacy-church-center"
      >
        {/* description is sanitized on the vertex, so we can live dangerously*/}
        <div dangerouslySetInnerHTML={{ __html: description }} />
      </div>
    </>
  )
}

function EventsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3" style={{ minHeight: "200px" }}>
      {children}
    </div>
  )
}

function LeadersIfAny() {
  const { data: leaders } = useFirstPageOfLeaders() as LeaderResponse
  const leadersHeader = leaders.length === 1 ? "Leader" : "Leaders"

  return (
    leaders.length > 0 && (
      <div className="mb-3">
        <Subheading level={2} text={leadersHeader} />
        <div className="mt-1">
          {leaders.map((leader) => (
            <p key={leader.id} className="fs-4 mb-0 c-tint1">
              {leader.attributes.first_name}
            </p>
          ))}
        </div>
      </div>
    )
  )
}

function LeftColumn({ children }: { children: React.ReactNode }) {
  return <div className="f-1 pr-4@md o-h">{children}</div>
}

function Location() {
  const { data: group } = useCurrentGroup() as GroupResponse
  const location = useLocationForCurrentGroup()

  return (
    <LocationDetails
      preference={group.attributes.location_type_preference}
      virtual={{ url: group.attributes.virtual_location_url }}
      physical={location?.attributes}
    />
  )
}

function PastEvents() {
  const {
    data: events = [],
    included,
    meta: { total_count: eventsCount },
  } = usePastEvents() as EventResponse
  const getEventUrl = (event: Event) => event.attributes.church_center_web_url
  const {
    data: { id: groupId },
  } = useCurrentGroup() as GroupResponse

  const {
    data: crossAppEvents,
    meta: { total_count: crossAppEventsCount },
  } = useApiRead(
    `/groups/v2/groups/${groupId}/cross_app_events?per_page=3&filter=past`,
  ) as CrossAppEventResponse

  const mergedEvents =
    crossAppEvents.length > 0
      ? events
          .concat(crossAppEvents)
          .sort(
            (a, b) =>
              new Date(b.attributes.starts_at).valueOf() -
              new Date(a.attributes.starts_at).valueOf(),
          )
          .slice(0, 3)
      : events

  return (
    <EventsList
      events={mergedEvents}
      included={included}
      getEventUrl={getEventUrl}
    >
      <EventsList.Content>
        <EventsList.Header>Past events</EventsList.Header>
        <EventsList.List />
      </EventsList.Content>
      {eventsCount + crossAppEventsCount > 3 && <ViewAll filter="past" />}
    </EventsList>
  )
}

function RightColumn({ children }: { children: React.ReactNode }) {
  return <div className="w-33%@md o-1@md fs-4 pt-4p">{children}</div>
}

function Schedule() {
  const { data: group } = useCurrentGroup() as GroupResponse

  return (
    <div className="mb-3 mb-4@md pt-2p">
      <Subheading level={2} text="Schedule" />
      <div className="mt-1">{group.attributes.schedule}</div>
    </div>
  )
}

function Tag({ tag }: { tag: Tag }) {
  const tagGroup = useTagGroupForTag(tag)

  return (
    <span className="badge">
      <strong>{tagGroup.attributes.name}</strong>: {tag.attributes.name}
    </span>
  )
}

function Tags({ tags }: { tags: Tag[] }) {
  return (
    <div className="mb-3">
      <Subheading level={2} text="Categories" />
      <div className="d-f fw-w g-4p mt-2">
        {tags.map((tag) => (
          <Tag key={tag.id} tag={tag} />
        ))}
      </div>
    </div>
  )
}

function UpcomingEvents() {
  const {
    data: events = [],
    included,
    meta: { total_count: eventsCount },
  } = useUpcomingEvents() as EventResponse
  const getEventUrl = (event: Event) => event.attributes.church_center_web_url
  const {
    data: { id: groupId },
  } = useCurrentGroup() as GroupResponse

  const {
    data: crossAppEvents,
    meta: { total_count: crossAppEventsCount },
  } = useApiRead(
    `/groups/v2/groups/${groupId}/cross_app_events?per_page=3&filter=upcoming`,
  ) as CrossAppEventResponse

  const promotedEvents = crossAppEvents
  const mergedEvents =
    promotedEvents.length > 0
      ? events
          .concat(promotedEvents)
          .sort(
            (a, b) =>
              new Date(a.attributes.starts_at).valueOf() -
              new Date(b.attributes.starts_at).valueOf(),
          )
          .slice(0, 3)
      : events

  return (
    <EventsList
      events={mergedEvents}
      included={included}
      getEventUrl={getEventUrl}
    >
      <EventsList.Content>
        <EventsList.Header>Upcoming events</EventsList.Header>
        <EventsList.List />
      </EventsList.Content>
      {eventsCount + crossAppEventsCount > 3 && <ViewAll filter="upcoming" />}
    </EventsList>
  )
}
function ViewAll({ filter }: { filter: "upcoming" | "past" }) {
  const { groupSlug } = useParams()

  return (
    <div className="mt-2 mb-1">
      <Link
        to={`/groups/groups/${groupSlug}/events/${filter}`}
        className="minor-btn secondary-btn btn"
      >
        View All
      </Link>
    </div>
  )
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return <div className="d-f@md mt-2 mt-0@sm">{children}</div>
}
